import React, {
  createContext,
  useContext,
  useReducer,
  PropsWithChildren,
  Dispatch
} from 'react';
import {
  SLIDE_EDITOR_ACTION_TYPE,
  slideEditorReducer
} from 'reducers/SlideEditorReducer';
import { Project } from 'data/projects';
import { PitchDeck } from 'data/pitchdecks';

export interface Image {
  x: number;
  y: number;
  url: string;
  width: number;
  height: number;
  roration: number;
  zIndex: number;
}

export interface Text {
  x: number;
  y: number;
  fontStyle: string;
  size: number;
  color: string;
  text: string;
  roration: number;
  alignment: string;
  font: string;
  width: number;
  zIndex: number;
}

export interface Slide {
  slideId?: string;
  background: string;
  backgroundPosition: {
    x: number;
    y: number;
  };
  backgroundScale?: number;
  images: Image[];
  texts: Text[];
}

export type SlideEditorView = 'mobile' | 'desktop';

export interface SlideEditorViewState {
  pitchDeckId: string;
  title: string;
  view: SlideEditorView;
  slide: Slide;
  statusId: string;
  trackingEnabled: boolean;
  openSettingsModal: boolean;
  projectData: Project | null;
  pitchDeckData: PitchDeck | null;
  activeSlideIndex: number;
  slideEditMode: boolean;
  editMode: boolean;
  slides: Slide[];
  showDeleteModal: boolean;
  uploading: boolean;
  updateBackground: boolean;
}

interface SlideEditorContextInterface extends SlideEditorViewState {
  slideEditorDispatch: Dispatch<SLIDE_EDITOR_ACTION_TYPE>;
}

export const SlideEditorContext = createContext(
  {} as SlideEditorContextInterface
);

const SlideEditorProvider = ({ children }: PropsWithChildren) => {
  const initialState: SlideEditorViewState = {
    pitchDeckId: '',
    title: '',
    view: 'mobile',
    slide: {
      background: '',
      images: [],
      texts: [],
      backgroundPosition: {
        x: 0,
        y: 0
      }
    },
    statusId: '',
    trackingEnabled: true,
    openSettingsModal: false,
    projectData: null,
    pitchDeckData: null,
    activeSlideIndex: 0,
    slideEditMode: false,
    editMode: false,
    slides: [],
    showDeleteModal: false,
    uploading: false,
    updateBackground: false
  };

  const [slideEditorState, slideEditorDispatch] = useReducer(
    slideEditorReducer,
    initialState
  );

  return (
    <SlideEditorContext.Provider
      value={{ ...slideEditorState, slideEditorDispatch }}
    >
      {children}
    </SlideEditorContext.Provider>
  );
};

export const useSlideEditorContext = () => useContext(SlideEditorContext);

export default SlideEditorProvider;
